import { defineStore } from 'pinia'
import axios from 'axios'
import { useNotificationStore } from './notification'

import { API_URL } from '@/config'

// „Zapamätať si ma“ nezaškrtnuté: prihlásenie platí len do zatvorenia prehliadača.
// Token ostáva v localStorage (čítajú ho aj ďalšie časti aplikácie), navyše sa uloží príznak
// a session cookie, ktorú prehliadač pri zatvorení zmaže. Pri ďalšom otvorení bez cookie token zahodíme.
const KLUC_LEN_RELACIA = 'token_len_relacia'
const COOKIE_RELACIE = 'intranet_relacia'

// Prebiehajúce overenie prihlásenia - App.vue aj router guard volajú checkAuth pri štarte naraz;
// zdieľajú jeden request /auth/me (inak dva requesty a pri neplatnom tokene dvojitá hláška)
let prebiehajuceOverenie = null

const maCookieRelacie = () =>
  document.cookie.split(';').some(c => c.trim() === `${COOKIE_RELACIE}=1`)

const nastavPrihlasenieLenNaRelaciu = (lenRelacia) => {
  try {
    if (lenRelacia) {
      document.cookie = `${COOKIE_RELACIE}=1; path=/; SameSite=Lax`
      // Ak má prehliadač vypnuté cookies, ostane trvalé prihlásenie (inak by odhlásil pri každom obnovení)
      if (maCookieRelacie()) {
        localStorage.setItem(KLUC_LEN_RELACIA, '1')
        return
      }
    }
    localStorage.removeItem(KLUC_LEN_RELACIA)
    document.cookie = `${COOKIE_RELACIE}=; path=/; max-age=0; SameSite=Lax`
  } catch (e) {
    // localStorage / cookies nedostupné - nič
  }
}

// Kontrola pri načítaní aplikácie (pred prvým použitím store)
try {
  if (localStorage.getItem(KLUC_LEN_RELACIA) === '1' && !maCookieRelacie()) {
    localStorage.removeItem('token')
    localStorage.removeItem(KLUC_LEN_RELACIA)
  }
} catch (e) {
  // localStorage nedostupné - nič
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    // Používateľské údaje a token
    user: null,
    token: localStorage.getItem('token') || null,
    loading: false,
    error: null
  }),

  getters: {
    // Kontrola, či je používateľ prihlásený
    isAuthenticated: (state) => !!state.token && !!state.user,
    
    // Kontrola, či je používateľ admin
    isAdmin: (state) => state.user?.rola === 'admin',
    
    // Vedenie firmy - rovnaké pravidlo ako backend (auth.middleware jeVedenie):
    // rola "manazment" alebo oddelenie Manažment / Riaditeľ (rola "manager" v DB neexistuje)
    jeVedenie: (state) =>
      !!state.user && (state.user.rola === 'manazment' || ['MANAZMENT', 'RIADITEL'].includes(state.user.oddelenie)),

    // Admin alebo vedenie (historický názov "manažér")
    isManager() {
      return this.isAdmin || this.jeVedenie;
    },
    
    // Prevádzka je príjemcom úloh, nie zadávateľom - aj keď má vyplnené oddelenie
    isPrevadzka: (state) => state.user?.rola === 'prevadzka',
    
    // Všetky predajne pridelené používateľovi (prevádzka ich môže mať viac)
    predajne: (state) => {
      if (!state.user) return [];
      if (Array.isArray(state.user.predajne) && state.user.predajne.length > 0) {
        return state.user.predajne;
      }
      return state.user.predajna ? [state.user.predajna] : [];
    },
    
    // ID pridelených predajní
    predajneIds() {
      return this.predajne.map(p => p.id);
    },
    
    // Názvy pridelených predajní
    predajneNazvy() {
      return this.predajne.map(p => p.nazov);
    },
    
    // Má používateľ pridelených viac predajní?
    maViacPredajni() {
      return this.predajne.length > 1;
    },
    
    // Oddelenie, pod ktoré spadajú úlohy zadané týmto používateľom
    oddelenieZadavatela: (state) =>
      state.user?.rola === 'prevadzka' ? null : (state.user?.oddelenie || null),
    
    // Môže zadávať úlohy na prevádzky - ktorékoľvek oddelenie okrem prevádzok
    canCreateUlohy: (state) => {
      if (!state.user || state.user.rola === 'prevadzka') return false;
      if (['admin', 'manazment', 'manazer', 'manager'].includes(state.user.rola)) return true;
      return !!state.user.oddelenie;
    },
    
    // Oddelenia viazané na pridelené prevádzky - zadávajú aj vidia len svoje prevádzky
    jeViazanyNaPredajne: (state) => state.user?.oddelenie === 'REGIONALNY_VEDUCI',
    
    // Vidí úlohy všetkých oddelení - len manažment a admin
    canManageUlohy: (state) => {
      if (!state.user || state.user.rola === 'prevadzka') return false;
      if (['admin', 'manazment', 'manazer', 'manager'].includes(state.user.rola)) return true;
      return ['MANAZMENT', 'RIADITEL'].includes(state.user.oddelenie);
    },
    
    // Dokumenty (interné príkazy, smernice) — rovnaké pravidlá ako backend (middleware/documents.middleware.js):
    // admin a vedenie (rola manazment, oddelenie Manažment / Riaditeľ) spravujú všetky,
    // regionálny vedúci môže nahrávať a upravovať iba dokumenty, ktoré sám nahral.
    mozeSpravovatVsetkyDokumenty: (state) => {
      const u = state.user;
      if (!u) return false;
      return u.rola === 'admin' || u.rola === 'manazment' || ['MANAZMENT', 'RIADITEL'].includes(u.oddelenie);
    },

    mozeNahravatDokumenty() {
      const u = this.user;
      if (!u) return false;
      return this.mozeSpravovatVsetkyDokumenty || (u.rola !== 'prevadzka' && u.oddelenie === 'REGIONALNY_VEDUCI');
    },

    // Funkcia: smie používateľ upraviť / zmazať / aktivovať konkrétny dokument?
    mozeUpravitDokument() {
      return (dokument) => {
        if (this.mozeSpravovatVsetkyDokumenty) return true;
        if (!this.mozeNahravatDokumenty || !dokument) return false;
        const autor = dokument.vytvoril_id ?? dokument.autor_id;
        return autor !== null && autor !== undefined && Number(autor) === Number(this.user.id);
      };
    },

    // Získanie celého mena používateľa
    fullName: (state) => state.user ? `${state.user.meno} ${state.user.priezvisko}` : '',
    
    // Získanie role používateľa v slovenčine
    roleName: (state) => {
      if (!state.user) return '';
      
      switch(state.user.rola) {
        case 'admin': return 'Administrátor';
        case 'manazment': return 'Manažment';
        case 'prevadzka': return 'Prevádzka';
        default: return 'Zamestnanec';
      }
    }
  },

  actions: {
    // Nastavenie auth tokenu pre axios
    setAuthHeader() {
      if (this.token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
      } else {
        delete axios.defaults.headers.common['Authorization'];
      }
    },
    
    // Prihlásenie používateľa
    // zapamatat = false („Zapamätať si ma“ nezaškrtnuté): prihlásenie platí do zatvorenia prehliadača
    async login(email, heslo, zapamatat = true) {
      const notificationStore = useNotificationStore();
      
      try {
        this.loading = true;
        this.error = null;
        
        // Reálne prihlásenie cez API
        const response = await axios.post('/auth/login', { email, heslo });
        
        if (response.data.success) {
          this.token = response.data.token;
          this.user = response.data.user;
          
          // Uloženie tokenu do local storage
          localStorage.setItem('token', this.token);
          nastavPrihlasenieLenNaRelaciu(!zapamatat);

          // Nastavenie auth hlavičky pre axios
          this.setAuthHeader();
          
          notificationStore.show({
            text: 'Prihlásenie úspešné',
            color: 'success'
          });
          
          return true;
        } else {
          throw new Error(response.data.message || 'Nesprávny email alebo heslo');
        }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Nastala chyba pri prihlásení';
        
        notificationStore.show({
          text: this.error,
          color: 'error'
        });
        
        return false;
      } finally {
        this.loading = false;
      }
    },
    
    // Odhlásenie používateľa
    async logout() {
      const notificationStore = useNotificationStore();
      
      try {
        if (this.token) {
          // Volanie API pre odhlásenie
          await axios.post('/auth/logout');
        }
      } catch (error) {
        console.error('Chyba pri odhlásení:', error);
      } finally {
        // Vyčistenie údajov
        this.clearAuth();

        notificationStore.show({
          text: 'Boli ste úspešne odhlásený',
          color: 'info'
        });
      }
    },

    // Lokálne vyčistenie prihlásenia bez volania API (vypršaný alebo neplatný token)
    clearAuth() {
      this.user = null;
      this.token = null;

      // Odstránenie tokenu z local storage
      localStorage.removeItem('token');
      nastavPrihlasenieLenNaRelaciu(false);

      // Odstránenie auth hlavičky z axios
      delete axios.defaults.headers.common['Authorization'];
    },

    // Kontrola autentifikácie používateľa
    checkAuth() {
      if (!prebiehajuceOverenie) {
        prebiehajuceOverenie = this.overitPrihlasenie().finally(() => {
          prebiehajuceOverenie = null
        })
      }
      return prebiehajuceOverenie
    },

    async overitPrihlasenie() {
      try {
        // Ak nemáme token, používateľ nie je prihlásený
        if (!this.token) {
          return false;
        }

        // Nastavenie auth hlavičky pre axios
        this.setAuthHeader();
        
        // Získanie informácií o prihlásenom používateľovi
        const response = await axios.get('/auth/me');
        
        if (response.data.success) {
          this.user = response.data.user;
          return true;
        } else {
          // Ak server vráti neúspech, odhlásime používateľa
          this.logout();
          return false;
        }
      } catch (error) {
        this.error = error.response?.data?.message || 'Chyba pri overovaní autentifikácie';
        // Odhlásime len pri neplatnom / expirovanom tokene alebo deaktivovanom účte (401).
        // Pri výpadku siete alebo chybe servera (napr. reštart backendu) token ponecháme -
        // po obnovení stránky bude používateľ znova prihlásený.
        if (error.response?.status === 401) {
          this.clearAuth();
          useNotificationStore().show({
            text: 'Vaše prihlásenie vypršalo. Prihláste sa znova.',
            color: 'warning'
          });
        }
        return false;
      } finally {
        this.loading = false;
      }
    },
    
    // Zmena hesla používateľa
    async changePassword(stareHeslo, noveHeslo) {
      const notificationStore = useNotificationStore();
      
      try {
        this.loading = true;
        this.error = null;
        
        const response = await axios.post(`${API_URL}/auth/change-password`, {
          stareHeslo,
          noveHeslo
        });
        
        if (response.data.success) {
          notificationStore.show({
            text: 'Heslo bolo úspešne zmenené',
            color: 'success'
          });
          
          return true;
        }
      } catch (error) {
        this.error = error.response?.data?.message || 'Nastala chyba pri zmene hesla';
        
        notificationStore.show({
          text: this.error,
          color: 'error'
        });
        
        return false;
      } finally {
        this.loading = false;
      }
    },
    
    // Zabudnuté heslo
    async forgotPassword(email) {
      const notificationStore = useNotificationStore();
      
      try {
        this.loading = true;
        this.error = null;
        
        const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });

        if (response.data.success) {
          // Hlášku určuje server (neprezradí, či účet existuje; ak sa e-mail neodoslal, vráti chybu 503)
          notificationStore.show({
            text: response.data.message || 'Ak existuje aktívny účet s týmto emailom, poslali sme naň odkaz na obnovenie hesla.',
            color: 'info',
            timeout: 8000
          });
          
          return true;
        }
      } catch (error) {
        this.error = error.response?.data?.message || 'Nastala chyba pri odosielaní emailu na obnovenie hesla';
        
        notificationStore.show({
          text: this.error,
          color: 'error'
        });
        
        return false;
      } finally {
        this.loading = false;
      }
    },
    
    // Reset hesla
    async resetPassword(token, noveHeslo) {
      const notificationStore = useNotificationStore();
      
      try {
        this.loading = true;
        this.error = null;
        
        const response = await axios.post(`${API_URL}/auth/reset-password`, {
          token,
          noveHeslo
        });
        
        if (response.data.success) {
          notificationStore.show({
            text: 'Heslo bolo úspešne obnovené. Môžete sa prihlásiť.',
            color: 'success'
          });
          
          return true;
        }
      } catch (error) {
        this.error = error.response?.data?.message || 'Nastala chyba pri obnovení hesla';
        
        notificationStore.show({
          text: this.error,
          color: 'error'
        });
        
        return false;
      } finally {
        this.loading = false;
      }
    },
    
    // Aktualizácia profilu používateľa
    async updateProfile(userData) {
      const notificationStore = useNotificationStore();
      
      try {
        this.loading = true;
        this.error = null;
        
        const response = await axios.put(`${API_URL}/users/${this.user.id}`, userData);
        
        if (response.data.success) {
          // Aktualizácia používateľských údajov v store
          this.user = response.data.data;
          
          notificationStore.show({
            text: 'Profil bol úspešne aktualizovaný',
            color: 'success'
          });
          
          return true;
        }
      } catch (error) {
        this.error = error.response?.data?.message || 'Nastala chyba pri aktualizácii profilu';
        
        notificationStore.show({
          text: this.error,
          color: 'error'
        });
        
        return false;
      } finally {
        this.loading = false;
      }
    },
    
    // Nahratie profilového obrázka
    async uploadProfileImage(formData) {
      const notificationStore = useNotificationStore();
      
      try {
        this.loading = true;
        this.error = null;
        
        const response = await axios.post(
          `${API_URL}/users/${this.user.id}/profile-image`, 
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        if (response.data.success) {
          // Aktualizácia cesty k profilovému obrázku
          this.user.profilovy_obrazok = response.data.data.profilovy_obrazok;
          
          notificationStore.show({
            text: 'Profilový obrázok bol úspešne nahraný',
            color: 'success'
          });
          
          return true;
        }
      } catch (error) {
        this.error = error.response?.data?.message || 'Nastala chyba pri nahrávaní profilového obrázka';
        
        notificationStore.show({
          text: this.error,
          color: 'error'
        });
        
        return false;
      } finally {
        this.loading = false;
      }
    }
  }
})
